import type {Delimiter, List, ListItem, Nodes} from 'mdast'
import type {Extension} from 'mdast-util-from-markdown'
import {visit} from 'unist-util-visit'

import {pushChild, removeChild, reverseChildrenEntries, unshiftChild} from '../../syntax-tree/tree-manipulation-utils'

/**
 * In HTML, lists (`ul` and `ol`) may _only_ contain list items (`li`). But in mdast by default, the list node will
 * include all tokens that appear between the list items, such as line endings, list item delimiters, and even blockquote
 * delimiters. To resolve this and allow us to easily generate a valid HTML structure, we push all of these things into
 * the corresponding list item. Most delimiters get pushed into the following item, except for line endings (which get
 * pushed into the preceding item) and anything at the end of the list (which gets pushed into the last item).
 *
 * @example
 * Before:
 * ```
 * list
 *   listItem
 *     delimiter_listItemPrefix
 *     content
 *   listItem
 *     delimiter_listItemPrefix
 *     content
 *     delimiter_listItemIndent
 *     list
 *       listItem
 *         delimiter_listItemPrefix
 *         content
 *       delimiter_listItemIndent
 *       listItem
 *         delimiter_listItemPrefix
 *         content
 * ```
 *
 * After:
 * ```
 * list
 *   listItem
 *     delimiter_listItemPrefix
 *     content
 *   listItem
 *     delimiter_listItemPrefix
 *     content
 *     list
 *       listItem
 *         delimiter_listItemIndent
 *         delimiter_listItemPrefix
 *         content
 *       listItem
 *         delimiter_listItemIndent
 *         delimiter_listItemPrefix
 *         content
 * ```
 */
export function mdastExpandListItems(): Extension {
  return {
    transforms: [expandListItems],
  }
}

function expandListItems(tree: Nodes): void {
  // First, move any listItemIndent immediately preceding a nested list into that list, where it will later be moved
  // into the first list item
  visit(tree, 'listItem', item => {
    let nextList: List | null = null

    for (const [i, child] of reverseChildrenEntries(item)) {
      if (child.type === 'list') {
        nextList = child
      } else if (
        child.type === 'delimiter' &&
        child.tokenType === 'listItemIndent' &&
        nextList &&
        // Multiline list items will contain indents in front of every line after the first. We only want to move the
        // ones that immediately precede a nested list.
        nextList.position?.start.offset === child.position?.end.offset
      ) {
        removeChild(item, i)
        unshiftChild(nextList, child)
      }
    }
  })

  // Then move anything not a list item into the list item that follows it
  visit(tree, 'list', list => {
    /** As we iterate through the children backwards, this will be the last item we encountered. */
    let nextListItem: ListItem | null = null
    let itemsAfterLastItem: Delimiter[] = []

    for (const [i, child] of reverseChildrenEntries(list)) {
      if (child.type === 'listItem') {
        // Expand the range of this item forward to fill any uncovered ranges. This covers text (esp line endings) between items
        const newEndPosition = nextListItem?.position?.start ?? list.position?.end
        if (child.position && newEndPosition) child.position.end = {...newEndPosition}

        // Push any items encountered at the end of the list
        if (itemsAfterLastItem.length) {
          for (const item of itemsAfterLastItem) pushChild(child, item)
          itemsAfterLastItem = []
        }

        nextListItem = child
      } else if (nextListItem) {
        // Everything else gets unshifted into the beginning of the list item that follows it
        removeChild(list, i)
        unshiftChild(nextListItem, child)
      } else {
        // There shouldn't be any non-listItem children after the last listItem. This is just in case.
        removeChild(list, i)
        itemsAfterLastItem.push(child)
      }
    }
  })
}
